import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Form, Icon } from 'semantic-ui-react'
import { debounce } from 'lodash'
import { API_BRANDS } from '../../../consts'
import { useBrand } from '../../../hooks/useBrand'
import { useBrands } from '../../../hooks/useBrands'
import { BrandCreateResponse } from '../../../redux/types/brandApi'
import { displayBrandAndStatus } from '../../../utils/Brands'
import { handleGenericErrors } from '../../../utils/Products/errorHandler'
import { to } from '../../../utils/to'

type BrandSelectOptions = { key: number; value: number; text: string }[]

export const BrandSelect = ({
  handleChange,
  productBrandId,
  productBrand,
}: {
  handleChange: (productBrandId?: number | string) => void
  productBrandId: string | number
  productBrand: string
}) => {
  const brandId = typeof productBrandId === 'number' ? productBrandId : parseInt(productBrandId)
  const { currentBrand } = useBrand(brandId)
  const [options, setOptions] = useState<BrandSelectOptions>([])
  const [searchQuery, setSearchQuery] = useState<string | undefined>()
  const [brandInputValue, setBrandInputValue] = useState<string | undefined>()
  const { brands, refresh } = useBrands(searchQuery)

  // When no matching brand is found and there is a productBrand, set
  // productBrand as search query value
  useEffect(() => {
    if (currentBrand === undefined && productBrand) {
      setSearchQuery(productBrand)
    } else if (currentBrand) {
      setSearchQuery('')
    }
  }, [currentBrand, setSearchQuery, productBrand])

  useEffect(() => {
    const results = brands.map((b) => ({
      key: b.id,
      value: b.id,
      text: displayBrandAndStatus(b),
    }))

    setOptions(results)
  }, [brands, setOptions])

  const debouncedSearch = useMemo(() => debounce(setSearchQuery, 300), [setSearchQuery])

  const onSearchChange = useCallback(
    (e: any, { searchQuery }: { searchQuery: string }) => {
      // we separated the search query from the input value so that we could debounce
      // just the search while still updating the input value.
      setBrandInputValue(searchQuery)
      debouncedSearch(searchQuery)
    },
    [debouncedSearch]
  )

  const onChange = (e: any, { value }: any) => {
    handleChange(value)
    setSearchQuery('')
  }

  const addBrand = async () => {
    const trimmedBrandName = searchQuery && searchQuery.trim()
    const request: AxiosRequestConfig = {
      url: API_BRANDS,
      method: 'POST',
      data: { name: trimmedBrandName },
      withCredentials: true,
    }

    const [error, response] = await to<AxiosResponse<BrandCreateResponse>, AxiosError>(
      axios(request)
    )
    if (error) {
      handleGenericErrors(error, 'Brand could not be created')
    } else {
      const id = response?.data.product_brand.id
      handleChange(id)
      setSearchQuery('')
    }
    await refresh()
  }

  const showCreate = searchQuery && brands.every((b) => b.name !== searchQuery)

  return (
    <>
      <Form.Field required>
        <label>Brand</label>
        {currentBrand ? (
          <Card
            color="blue"
            fluid
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Card.Content
              data-testid="BrandSelct#card-content"
              header={displayBrandAndStatus(currentBrand) || '[No Brand]'}
              description={`#${currentBrand.id}`}
              style={{ flex: '7', wordBreak: 'break-all' }}
            />
            <Card.Content style={{ flex: '1' }}>
              <Button
                type="button"
                icon
                onClick={(_e: any) => {
                  _e.preventDefault()
                  handleChange('')
                }}
              >
                <Icon name="trash" size="large" color="red" style={{ cursor: 'pointer' }} />
              </Button>
            </Card.Content>
          </Card>
        ) : (
          <Card color="red" fluid description="This product does not have a brand assigned." />
        )}

        <Form.Select
          data-testid="BrandSelect"
          selectOnBlur={false}
          selectOnNavigation={false}
          fluid
          placeholder="Search Brands..."
          selection
          search={(options, query) => options}
          deburr
          noResultsMessage={null}
          searchQuery={brandInputValue}
          onSearchChange={onSearchChange}
          options={options}
          onChange={onChange}
        />

        <p>
          {showCreate && (
            <>
              <Button
                fluid
                onClick={addBrand}
                type="button"
                data-testid="BrandSelect#create-button"
              >
                Create new brand: {searchQuery}
              </Button>
            </>
          )}
        </p>
      </Form.Field>
    </>
  )
}
